import { Badge, Skeleton } from '@vnetwork/ui/primitives';
import { EmptyState, Timeline } from '@vnetwork/ui/data';
import type { TimelineItem } from '@vnetwork/ui/data';
import { useNegotiationHistories, type NegotiationHistory } from '@/api/customer-journey';
import { formatDateTime, formatMoney } from '@/lib/format';
import { negotiationTone } from './sale-process-helpers';

/**
 * History log for a selected negotiation — each round's status, note, value and
 * author, newest first. Rendered via @ui Timeline. Loads on demand for the
 * selected negotiation id.
 */

function statusLabel(status?: string): string {
  if (!status) return '—';
  return status.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function historyToTimelineItem(row: NegotiationHistory, index: number): TimelineItem {
  return {
    id: row.contract_negotiation_history_id ?? index,
    title: (
      <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
        <Badge tone={negotiationTone(row.history_status)} appearance="subtle" size="sm">
          {statusLabel(row.history_status)}
        </Badge>
        {row.history_value ? (
          <span style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 'var(--vn-font-weight-semibold)' }}>
            {formatMoney(row.history_value)}
          </span>
        ) : null}
      </span>
    ),
    timestamp: (
      <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-subtle)' }}>
        {formatDateTime(row.created_at)}
      </span>
    ),
    meta: row.employee_name ? (
      <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
        {row.employee_name}
      </span>
    ) : undefined,
    description: row.history_note ? (
      <p style={{ margin: 0, fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
        {row.history_note}
      </p>
    ) : undefined,
    tone: 'neutral',
  };
}

export function NegotiationHistoryPanel({ negotiationId }: { negotiationId: number }) {
  const query = useNegotiationHistories(negotiationId);
  const rows = query.data?.results ?? [];

  if (query.isLoading) {
    return (
      <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} style={{ height: 20 }} />
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return <EmptyState title="No history yet" description="Negotiation rounds will appear here." />;
  }

  return <Timeline items={rows.map(historyToTimelineItem)} size="sm" />;
}

export default NegotiationHistoryPanel;
